import { capitalize } from "lodash";
import { DateTime } from "luxon";
import { DateValueType } from "react-tailwindcss-datepicker";
import type { CRMNote, NotePriority, NoteVisibility } from "@/types";
import { NotePriorityValues, NoteNotableValues } from "@/types";

const NOTE_PRIORITY_DISPLAY_NAMES: Record<string, string> = {
  [NotePriorityValues.NONE]: "None",
  [NotePriorityValues.PROMOTE]: "Pinned",
  [NotePriorityValues.ALERT]: "Pinned/Alert"
};

export function formatNotePriority(priority: NotePriority | null | undefined): string {
  return NOTE_PRIORITY_DISPLAY_NAMES[priority || NotePriorityValues.NONE];
}

/**
 * Formats a NoteVisibility value for display by capitalizing it.
 * @param visibility - The visibility value to format
 * @returns Formatted visibility string (e.g., "Internal", "External")
 */
export function formatNoteVisibility(visibility: NoteVisibility): string {
  return capitalize(visibility.toLowerCase());
}

/**
 * Converts a date string to DateValueType for use with DateSelect component.
 * Uses Luxon to handle timezone properly and ensure local midnight.
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @returns DateValueType object with startDate and endDate set to the same date, or empty string if invalid
 */
export function dateStringToDateValueType(dateString: string | null | undefined): DateValueType | string {
  if (!dateString) {
    return "";
  }

  const dateTime = DateTime.fromISO(dateString).startOf("day");
  if (dateTime.isValid) {
    const date = dateTime.toJSDate();
    return {
      startDate: date,
      endDate: date
    };
  }

  return "";
}

const NOTABLE_TYPE_DISPLAY_NAMES: Record<string, string> = {
  [NoteNotableValues.EMPLOYMENT]: "Employment",
  [NoteNotableValues.EVENT]: "Event",
  [NoteNotableValues.EVENT_REGISTRATION]: "Event Registration",
  [NoteNotableValues.FACILITY]: "Facility",
  [NoteNotableValues.FACILITY_ASSOCIATION]: "Facility Governance",
  [NoteNotableValues.INTERACTION]: "Interaction",
  [NoteNotableValues.MEMBERSHIP_APPLICATION]: "Application",
  [NoteNotableValues.MEMBERSHIP_BAND]: "Membership",
  [NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST]: "Change Request",
  [NoteNotableValues.MEMBERSHIP_SUSPENSION]: "Suspension",
  [NoteNotableValues.ORDER]: "Order",
  [NoteNotableValues.PAYMENT]: "Payment",
  [NoteNotableValues.PERSON]: "Person",
  [NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT]: "University Student",
  [NoteNotableValues.UNIVERSITY_PROBATION]: "University Student Probation"
};

export function formatSourceName(notableType: string | null): string {
  if (!notableType) return "";
  if (NOTABLE_TYPE_DISPLAY_NAMES[notableType]) return NOTABLE_TYPE_DISPLAY_NAMES[notableType];
  if (!notableType.includes("_")) return notableType;
  return notableType
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

interface NoteParentLinkContext {
  pgaId: string;
  universityLegacyId?: string;
  facilityId?: string;
  eventSlug?: string;
  /** Event detail base path (`/events`, `/activities/events`, or `/education/universities/:id/events`). Defaults to `/events`. */
  eventBasePath?: string;
}

/**
 * Builds a parent link URL for a CRM note based on its notableType.
 * Used by the Source column and the NoteSideSheet to navigate to the parent entity.
 *
 * @param note - The CRM note to build a link for
 * @param context - Context needed to build links (pgaId, universityLegacyId, etc.)
 * @returns A URL string with query params for scroll/expand/highlight, or null if unmapped
 */
export function getNoteParentLink(
  note: Pick<CRMNote, "id" | "notableId" | "notableType" | "eventSlug">,
  context: string | NoteParentLinkContext
): string | null {
  // Support legacy string signature (pgaId only) and new object signature
  const ctx: NoteParentLinkContext = typeof context === "string" ? { pgaId: context } : context;

  if (!note.notableId) return null;

  if (note.notableType === NoteNotableValues.FACILITY) {
    return `/facilities/${note.notableId}/details/notes?highlightNote=${note.id}`;
  }

  if (note.notableType === NoteNotableValues.EVENT) {
    const eventSlug = note.eventSlug ?? ctx.eventSlug;
    if (!eventSlug) return null;
    const eventBasePath = ctx.eventBasePath ?? "/events";
    return `${eventBasePath}/${eventSlug}/notes?highlightNote=${note.id}`;
  }

  if (note.notableType === NoteNotableValues.FACILITY_ASSOCIATION) {
    if (!ctx.facilityId) return null;
    return `/facilities/${ctx.facilityId}/people/governance?expandNotes=${note.notableId}&highlightNote=${note.id}`;
  }

  if (note.notableType === NoteNotableValues.EVENT_REGISTRATION) {
    const eventSlug = note.eventSlug ?? ctx.eventSlug;
    if (!eventSlug) return null;
    return `/activities/events/${eventSlug}/registrations/${note.notableId}?highlightNote=${note.id}`;
  }

  if (!ctx.pgaId) return null;

  switch (note.notableType) {
    case NoteNotableValues.EMPLOYMENT:
      return `/people/${ctx.pgaId}/career/employment?expandNotes=${note.notableId}&highlightNote=${note.id}`;
    case NoteNotableValues.MEMBERSHIP_APPLICATION:
      return `/people/membership-services/applications/${note.notableId}/notes?highlightNote=${note.id}`;
    case NoteNotableValues.INTERACTION:
      return `/people/${ctx.pgaId}/interactions/interactions/${note.notableId}?highlightNote=${note.id}`;
    case NoteNotableValues.PERSON:
      return `/people/${ctx.pgaId}/person/notes?highlightNote=${note.id}`;
    case NoteNotableValues.MEMBERSHIP_BAND:
      return `/people/${ctx.pgaId}/membership/overview?expandNotes=${note.notableId}&highlightNote=${note.id}`;
    case NoteNotableValues.MEMBERSHIP_SUSPENSION:
      return `/people/${ctx.pgaId}/membership/overview?scrollTo=suspension-history&expandSuspensionNotes=${note.notableId}&highlightSuspensionNote=${note.id}`;
    case NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST:
      return `/people/${ctx.pgaId}/person/change-requests?expandNotes=${note.notableId}&highlightNote=${note.id}`;
    case NoteNotableValues.ORDER:
      return `/billing/orders/${note.notableId}?highlightNote=${note.id}`;
    case NoteNotableValues.PAYMENT:
      return `/billing/orders/payment/${note.notableId}?highlightNote=${note.id}`;
    case NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT:
      if (!ctx.universityLegacyId) return null;
      return `/education/universities/${ctx.universityLegacyId}/students/${ctx.pgaId}/notes?highlightNote=${note.id}`;
    case NoteNotableValues.UNIVERSITY_PROBATION:
      if (!ctx.universityLegacyId) return null;
      return `/education/universities/${ctx.universityLegacyId}/students/${ctx.pgaId}?expandProbationNotes=${note.notableId}&highlightNote=${note.id}`;
    default:
      return null;
  }
}

/**
 * Builds the path to the "all notes" page for a person, detecting whether
 * the current route is inside a student/university context or a top-level person context.
 */
export function getNotesPagePath(pgaId: string, pathname: string): string {
  const studentRouteMatch = pathname.match(/^\/education\/universities\/(\d+)\/students\/[^/]+/);
  return studentRouteMatch
    ? `/education/universities/${studentRouteMatch[1]}/students/${pgaId}/notes`
    : `/people/${pgaId}/person/notes`;
}

/**
 * Converts a DateValueType startDate to an ISO date string (YYYY-MM-DD).
 * Handles both Date objects and string values.
 * @param dateValue - The DateValueType object containing startDate
 * @returns ISO date string (YYYY-MM-DD) or null if no valid date
 */
export function dateValueTypeToISOString(dateValue: DateValueType | string | null | undefined): string | null {
  if (!dateValue || typeof dateValue === "string") {
    return null;
  }

  if (typeof dateValue === "object" && dateValue.startDate) {
    if (dateValue.startDate instanceof Date) {
      return dateValue.startDate.toISOString().split("T")[0];
    }
    if (typeof dateValue.startDate === "string") {
      return new Date(dateValue.startDate).toISOString().split("T")[0];
    }
  }

  return null;
}

export const NOTE_PRIORITY_OPTIONS = [
  { value: NotePriorityValues.NONE, label: formatNotePriority(NotePriorityValues.NONE) },
  { value: NotePriorityValues.PROMOTE, label: formatNotePriority(NotePriorityValues.PROMOTE) },
  { value: NotePriorityValues.ALERT, label: formatNotePriority(NotePriorityValues.ALERT) }
];

export function buildNoteInput(
  content: string | undefined,
  priority: NotePriority | undefined,
  title: string,
  priorityExpirationDate?: string | null
) {
  if (!content) return undefined;
  return {
    content,
    priority: priority || NotePriorityValues.NONE,
    title,
    ...(priorityExpirationDate && { priorityExpirationDate })
  };
}
